import React, { useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import {ArrowRight, CheckCircle, AlertTriangle, Download, Printer} from 'lucide-react'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'

interface Question {
  id: number
  text: string
}

const gad7Questions: Question[] = [
  { id: 1, text: 'Feeling nervous, anxious, or on edge' },
  { id: 2, text: 'Not being able to stop or control worrying' },
  { id: 3, text: 'Worrying too much about different things' },
  { id: 4, text: 'Trouble relaxing' },
  { id: 5, text: 'Being so restless that it\'s hard to sit still' },
  { id: 6, text: 'Becoming easily annoyed or irritable' },
  { id: 7, text: 'Feeling afraid as if something awful might happen' }
]

const scoreOptions = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' }
]

export default function GAD7Assessment() {
  const [answers, setAnswers] = useState<{ [key: number]: number }>({})
  const [showResults, setShowResults] = useState(false)
  const resultsRef = useRef<HTMLDivElement>(null)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

  const handleAnswer = (questionId: number, value: number) => {
    setAnswers({ ...answers, [questionId]: value })
  }

  const calculateScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0)
  }

  const handleSubmit = () => {
    if (Object.keys(answers).length === 7) {
      setShowResults(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const handleDownloadPDF = async () => {
    if (!resultsRef.current) return
    
    setIsGeneratingPDF(true)
    try {
      const canvas = await html2canvas(resultsRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff'
      })
      
      const imgData = canvas.toDataURL('image/png')
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })
      
      const imgWidth = 210
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight)
      pdf.save(`GAD7-Results-${new Date().toISOString().split('T')[0]}.pdf`)
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert('Failed to generate PDF. Please try again or use the Print option.')
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const getScoreInterpretation = (score: number) => {
    if (score <= 4) {
      return {
        level: 'Minimal Anxiety',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: 'Your responses suggest minimal anxiety symptoms. Continue monitoring your mental health and practice self-care.',
        recommendation: 'Maintain healthy habits like regular exercise, adequate sleep, and stress management techniques.'
      }
    } else if (score <= 9) {
      return {
        level: 'Mild Anxiety',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        description: 'Your responses indicate mild anxiety. While manageable, consider lifestyle changes and monitoring symptoms.',
        recommendation: 'Try relaxation techniques, mindfulness, and consider speaking with a healthcare provider if symptoms persist.'
      }
    } else if (score <= 14) {
      return {
        level: 'Moderate Anxiety',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        description: 'Your responses suggest moderate anxiety that may be impacting your daily life.',
        recommendation: 'We recommend consulting with a mental health professional. Therapy, particularly CBT, can be very effective.'
      }
    } else {
      return {
        level: 'Severe Anxiety',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        description: 'Your responses indicate severe anxiety symptoms that likely interfere significantly with daily activities.',
        recommendation: 'Please consult with a mental health professional as soon as possible. Treatment with therapy and/or medication can provide substantial relief.'
      }
    }
  }

  const score = calculateScore()
  const interpretation = getScoreInterpretation(score)
  const isComplete = Object.keys(answers).length === 7

  return (
    <div>
      {/* Results Section */}
      {showResults && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6 print:hidden">
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF Report
                  </>
                )}
              </button>
              <button
                onClick={handlePrint}
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-md"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Report
              </button>
            </div>
            
            <div ref={resultsRef} className={`${interpretation.bgColor} border-2 ${interpretation.borderColor} rounded-xl p-8`}>
              <div className="text-center mb-6">
                <div className="text-6xl font-bold mb-2 text-gray-900">{score}</div>
                <div className={`text-2xl font-bold ${interpretation.color}`}>{interpretation.level}</div>
              </div>
              <div className="bg-white rounded-lg p-6 mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Your Results</h3>
                <p className="text-gray-700 mb-4">{interpretation.description}</p>
                <p className="text-gray-700"><strong>Recommendation:</strong> {interpretation.recommendation}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
                <Link
                  to="/therapy"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#4A90E2] text-white rounded-lg font-semibold hover:bg-[#3A7BC8] transition-all"
                >
                  Find a Therapist
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <button
                  onClick={() => {
                    setAnswers({})
                    setShowResults(false)
                  }}
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-[#4A90E2] border-2 border-[#4A90E2] rounded-lg font-semibold hover:bg-blue-50 transition-all"
                >
                  Retake Assessment
                </button>
              </div>
              
              <div className="hidden print:block mt-8 pt-6 border-t border-gray-300">
                <p className="text-sm text-gray-600 text-center">
                  Generated by Anxiety USA - anxietyusa.com | Date: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* About GAD-7 */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">About the GAD-7 Assessment</h2>
          <div className="prose prose-lg max-w-none text-gray-700 mb-8">
            <p className="mb-4">
              The <strong>GAD-7</strong> is a widely-used clinical tool developed by Drs. Spitzer, Kroenke, Williams, and Löwe. It helps identify probable cases of Generalized Anxiety Disorder and assess symptom severity over the past two weeks.
            </p>
            <p className="mb-4">
              This self-assessment takes just 2-3 minutes and provides immediate insights into your anxiety levels. Healthcare providers across the United States use this same screening tool during <strong>GAD diagnosis</strong> evaluations.
            </p>
          </div>

          <div className="bg-blue-50 border-l-4 border-[#4A90E2] p-6 rounded-r-lg mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Understanding Your Score</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="font-bold text-green-600 w-16 flex-shrink-0">0-4:</div>
                <div className="text-gray-700">Minimal anxiety</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-yellow-600 w-16 flex-shrink-0">5-9:</div>
                <div className="text-gray-700">Mild anxiety</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-orange-600 w-16 flex-shrink-0">10-14:</div>
                <div className="text-gray-700">Moderate anxiety</div>
              </li>
              <li className="flex items-start">
                <div className="font-bold text-red-600 w-16 flex-shrink-0">15-21:</div>
                <div className="text-gray-700">Severe anxiety</div>
              </li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-12">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-900 mb-2">Important Disclaimer</h4>
                <p className="text-sm text-gray-700">
                  This screening tool is not a diagnostic instrument. Only a qualified healthcare professional can provide a GAD diagnosis. If you're experiencing significant distress, please consult with a licensed mental health provider.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GAD-7 Questionnaire */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">GAD-7 Screening Tool</h2>
            <p className="text-center text-gray-600 mb-8">
              Over the last 2 weeks, how often have you been bothered by the following problems?
            </p>

            <div className="space-y-8">
              {gad7Questions.map((question) => (
                <div key={question.id} className="pb-6 border-b border-gray-200 last:border-b-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {question.id}. {question.text}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {scoreOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleAnswer(question.id, option.value)}
                        className={`p-4 rounded-lg border-2 transition-all text-center ${
                          answers[question.id] === option.value
                            ? 'border-[#4A90E2] bg-blue-50 text-[#4A90E2] font-semibold'
                            : 'border-gray-200 hover:border-[#48C9B0] text-gray-700'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={handleSubmit}
                disabled={!isComplete}
                className={`inline-flex items-center justify-center px-8 py-4 rounded-lg font-semibold transition-all ${
                  isComplete
                    ? 'bg-[#4A90E2] text-white hover:bg-[#3A7BC8] shadow-lg cursor-pointer'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isComplete ? (
                  <>
                    Calculate My Score
                    <CheckCircle className="ml-2 w-5 h-5" />
                  </>
                ) : (
                  <>Answer all questions to see results</>
                )}
              </button>
              <p className="text-sm text-gray-500 mt-4">
                {Object.keys(answers).length} of 7 questions answered
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About GAD-2 */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">GAD-2: The Quick Screening Alternative</h2>
          <p className="text-lg text-gray-700 mb-6">
            The <strong>GAD-2</strong> is an ultra-brief, 2-question version of the GAD-7 designed for rapid anxiety screening in busy clinical settings. It uses the first two questions from the full GAD-7:
          </p>
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <ol className="list-decimal list-inside space-y-3 text-gray-700">
              <li>Feeling nervous, anxious, or on edge</li>
              <li>Not being able to stop or control worrying</li>
            </ol>
          </div>
          <p className="text-lg text-gray-700 mb-6">
            A score of 3 or higher on the GAD-2 (out of 6 possible points) suggests clinically significant anxiety and warrants further evaluation with the full GAD-7 or a clinical interview.
          </p>
        </div>
      </section>
    </div>
  )
}